/**
 * @file MeetingRowActions.tsx
 * @brief Row/detail "…" menu for a MeetingIntel meeting — company-admin manage.
 *
 * @details
 * The web equivalent of the mobile MeetingDetails action sheet, scoped to what
 * a company admin can do on a company member's meeting (the backend widened
 * these endpoints on 08-07-2026):
 *   - View details        → navigate / callback to the detail page
 *   - Export MoM PDF       → POST export-mom, opens the signed PDF in a new tab
 *   - Export transcript    → POST export-transcript, original spoken language
 *                            only (no language picker since 15-10-2026, ADR-037)
 *   - Regenerate intel     → POST regenerate-intelligence (re-runs synthesis)
 *   - Reprocess diarization→ POST reprocess-diarization (resets + re-transcribes)
 *     Both are Super Admin only since 22-10-2026 (DES, cost control); every
 *     scope gets "Retry processing" on a failed (`error`) meeting only.
 *   - Delete meeting       → DELETE session + GCS assets
 *
 * Heavy/destructive ops (regenerate, reprocess, delete) are each guarded by an
 * `<AlertDialog>`. Export and gating follow the meeting's `status` /
 * `can_delete` / `recording_url` so we never offer an action the backend will
 * reject. Stops row-click propagation so opening the menu never also opens the
 * row's detail page.
 *
 * @dependencies
 * - features/company-meetings/api (manage mutations)
 * - components/ui/{DropdownMenu, AlertDialog, Dialog, Button}
 * - shared/hooks/useToast
 *
 * @references
 * - BACKEND/src/features/boardroom/routes.js
 * - features/sessions/components/SessionRowActions.tsx (pattern)
 *
 * @author DES
 * @date 08-07-2026
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Ellipsis,
  Eye,
  PenLine,
  ArrowDownToLine,
  LoaderCircle,
  MessagesSquare,
  RefreshCw,
  RotateCcw,
  Share2,
  Sparkles,
  Tag,
  Trash2,
  UserRoundPen,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { deferAfterDropdownMenu } from "@/shared/utils/restoreBodyInteraction";
import {
  useDeleteCompanyMeeting,
  useExportCompanyMeetingMom,
  useExportCompanyMeetingTranscript,
  useRegenerateCompanyMeetingIntelligence,
  useReprocessCompanyMeetingDiarization,
} from "../api";
import {
  useDeleteAdminMeeting,
  useRegenerateAdminMeetingIntelligence,
  useReprocessAdminMeetingDiarization,
} from "@/features/admin-meetings/api";
import { isNotesTierMeeting, type MeetingRow } from "../schemas";
import { useMeetingExport } from "@/shared/auth/meExtras";
import { MeetingShareItems } from "@/features/employee-boardroom/MeetingShareItems";
import { meetingShareRights } from "@/features/employee-boardroom/sharingLine";

/** Which boardroom scope the manage actions operate in. `company` and
 *  `employee` share `/api/boardroom/*`; `admin` uses platform admin routes. */
export type MeetingActionsScope = "company" | "admin" | "employee";

/** Regenerate on a Meeting Notes recording (ADR-030): the Notes pipeline. */
const NOTES_REGENERATE_COPY = {
  title: "Regenerate meeting notes?",
  description:
    "Writes the summary and to-dos again from the existing transcript. Ticks on to-dos from the recording are lost; hand-added to-dos are kept. The recording and transcript are untouched.",
  action: "Regenerate",
  successTitle: "Notes are being written again",
};

// ----------------------------------------------------------------------
// Confirm-guarded variants (regenerate / reprocess / delete)
// ----------------------------------------------------------------------
type ConfirmVariant = "regenerate" | "reprocess" | "delete" | "retry";

const CONFIRM_COPY: Record<
  ConfirmVariant,
  { title: string; description: string; action: string; successTitle: string }
> = {
  regenerate: {
    title: "Regenerate meeting intelligence?",
    description:
      "Re-runs AI synthesis from the existing transcript, overwriting the current summary, decisions, action items, and mindmap. Ticks and reassignments on action items from the recording are lost too; hand-added to-dos are kept. The recording and transcript are untouched.",
    action: "Regenerate",
    successTitle: "Synthesis restarted",
  },
  reprocess: {
    title: "Reprocess diarization?",
    description:
      "Resets speaker tracks and the transcript, then re-runs transcription on the original recording. Any manual speaker tagging and current intelligence will be rebuilt from scratch.",
    action: "Reprocess",
    successTitle: "Reprocessing started",
  },
  retry: {
    title: "Retry processing?",
    description: "Processes this meeting again from its recording. It can take a minute or two.",
    action: "Retry",
    successTitle: "Started. This can take a minute or two.",
  },
  delete: {
    title: "Delete this meeting?",
    description:
      "Permanently deletes the recording, transcript, intelligence, and all cloud assets for this meeting. This cannot be undone.",
    action: "Delete meeting",
    successTitle: "Meeting deleted",
  },
};

interface MeetingRowActionsProps {
  meeting: Pick<
    MeetingRow,
    | "id"
    | "status"
    | "can_delete"
    | "recording_url"
    | "mom_pdf_url"
    | "target_languages"
    | "participants"
    | "attendees"
    | "tier"
    | "sharing"
  >;
  /** Scope selects which endpoints the manage actions call. Defaults to the
   *  company-admin scope. */
  scope?: MeetingActionsScope;
  /** Open the detail page (row-menu parity with a row click). Omit on the
   *  detail page itself where "View" is redundant. */
  onView?: (id: string) => void;
  /** Fired after a successful delete so the parent can close/redirect. */
  onDeleted?: (id: string) => void;
  /** Owner actions from the phone's long-press sheet (30-09-2026). Shown only
   *  when passed, so the admin table and detail menus are unchanged. */
  onEditTags?: () => void;
  /** Tags as a submenu of this menu (`TagsSubmenu`), in place of the
   *  "Tags" item that opened a dialog (DES 04-10-2026). */
  tagsMenu?: ReactNode;
  /** The share panel (`MeetingShareMenu`), opened as a submenu of this menu
   *  so it looks and opens like it (DES 30-09-2026). */
  shareMenu?: ReactNode;
  /** "Share…" and "Who has access" from the list item's `sharing`
   *  (`MeetingShareItems`, DES 22-10-2026): the meeting lists pass it, the
   *  meeting page has its own Share pill. */
  withShare?: boolean;
  /** Bumped by the phone-style detail's Export pill (30-09-2026) to export the
   *  transcript PDF. Always the original spoken language (DES 15-10-2026,
   *  ADR-037). 0 / undefined = never. */
  transcriptExportSignal?: number;
  /** MoM language for export-mom (matches language pills) (13-07-2026). */
  targetLanguage?: string;
  /** "Name speakers" on a Meeting Notes meeting (22-10-2026): the meeting
   *  page passes it for the owner only. */
  onNameSpeakers?: () => void;
  /** B48 "Rename meeting" (02-11-2026; U5 label): the meeting page passes it for the owner. */
  onRename?: () => void;
  /** U5 "Reset layout" (03-11-2026): Meeting Intelligence's Intel + Points
   *  sections back to today's order, all open. The meeting page passes it. */
  onResetLayout?: () => void;
  /** Greyed when nothing is saved. */
  resetLayoutDisabled?: boolean;
}

export function MeetingRowActions({
  meeting,
  scope = "company",
  onView,
  onDeleted,
  onEditTags,
  tagsMenu,
  shareMenu,
  withShare = false,
  transcriptExportSignal,
  targetLanguage,
  onNameSpeakers,
  onRename,
  onResetLayout,
  resetLayoutDisabled = false,
}: MeetingRowActionsProps) {
  const [variant, setVariant] = useState<ConfirmVariant | null>(null);

  // Both scopes' hooks are called unconditionally (rules of hooks); we select
  // the active set by `scope`. Mirrors features/sessions SessionRowActions.
  const delCompany = useDeleteCompanyMeeting();
  const regenCompany = useRegenerateCompanyMeetingIntelligence();
  const reprocessCompany = useReprocessCompanyMeetingDiarization();
  const exportMomCompany = useExportCompanyMeetingMom();
  const exportTranscriptCompany = useExportCompanyMeetingTranscript();
  const delAdmin = useDeleteAdminMeeting();
  const regenAdmin = useRegenerateAdminMeetingIntelligence();
  const reprocessAdmin = useReprocessAdminMeetingDiarization();
  const isAdmin = scope === "admin";
  const del = isAdmin ? delAdmin : delCompany;
  const regen = isAdmin ? regenAdmin : regenCompany;
  const reprocess = isAdmin ? reprocessAdmin : reprocessCompany;
  // VERBLIZR staff never export meeting content (B2/W4, DES 22-10-2026):
  // the admin scope has no export, and its export routes answer 403.
  const exportMom = exportMomCompany;
  const exportTranscript = exportTranscriptCompany;

  const id = meeting.id;
  const isReady = meeting.status === "ready";
  // Meeting Notes (ADR-030, 06-10-2026): no PDF minutes, and no "Reprocess
  // diarization" (a second paid transcription of a meeting whose upgrade path
  // already re-runs it). Regenerate stays: it re-runs the one Notes call.
  const isNotes = isNotesTierMeeting(meeting);
  const isBusy =
    meeting.status === "processing" ||
    meeting.status === "synthesis_processing" ||
    meeting.status === "uploading";
  const canDelete = meeting.can_delete !== false;
  const hasRecording =
    Boolean(meeting.recording_url) ||
    (meeting as { has_recording?: boolean | null }).has_recording === true;
  // Retry on a failed meeting (22-10-2026): Notes re-run their one Notes call;
  // a full meeting re-runs from the recording (meetingFailure.ts), or compiles
  // again when there is no recording to re-run.
  const isFailed = meeting.status === "error";
  const retryReprocess = !isNotes && hasRecording;
  const retryMutation = retryReprocess ? reprocess : regen;
  // ADR-051 (21-10-2026): exports off for this member (their company, or the
  // meeting owner's). The super admin's read-only scope is never limited.
  const meetingExport = useMeetingExport(meeting);
  const exportOk = !isAdmin && meetingExport.canExport;
  const canExportTranscript =
    meeting.status === "ready" ||
    meeting.status === "resolved_pending" ||
    meeting.status === "synthesis_processing";

  const activeMutation =
    variant === "delete"
      ? del
      : variant === "regenerate"
        ? regen
        : variant === "reprocess"
          ? reprocess
          : variant === "retry"
            ? retryMutation
            : null;
  const copy = variant
    ? variant === "regenerate" && isNotes
      ? NOTES_REGENERATE_COPY
      : CONFIRM_COPY[variant]
    : null;

  const handleExport = async () => {
    if (!exportOk) return;
    try {
      const res = await exportMom.mutateAsync({
        id,
        ...(targetLanguage ? { targetLanguage } : {}),
      });
      if (res.momPdfUrl) window.open(res.momPdfUrl, "_blank", "noopener");
      toast({ variant: "success", title: isNotes ? "Notes PDF ready" : "MoM PDF ready" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Export failed",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  // Transcript PDF = the original spoken language, no language choice
  // (DES 15-10-2026, ADR-037). The server renders the stored rows as is.
  const handleExportTranscript = async () => {
    if (exportTranscript.isPending) return;
    if (!exportOk) {
      // The meeting page's Export → Transcript lands here too.
      toast({ variant: "destructive", title: "Export is off", description: meetingExport.exportLine ?? undefined });
      return;
    }
    try {
      const res = await exportTranscript.mutateAsync({ id });
      if (res.transcriptPdfUrl) {
        window.open(res.transcriptPdfUrl, "_blank", "noopener");
      }
      toast({ variant: "success", title: "Transcript PDF ready" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Transcript export failed",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };
  const exportTranscriptRef = useRef(handleExportTranscript);
  exportTranscriptRef.current = handleExportTranscript;
  useEffect(() => {
    if (transcriptExportSignal) void exportTranscriptRef.current();
  }, [transcriptExportSignal]);

  const handleConfirm = async () => {
    if (!variant) return;
    try {
      if (variant === "delete") {
        await del.mutateAsync(id);
      } else if (variant === "retry") {
        if (retryReprocess) await reprocess.mutateAsync(id);
        else await regen.mutateAsync({ id, force: true });
      } else if (variant === "regenerate") {
        // force: true — the user explicitly confirmed an overwrite, so bypass the
        // "already_upgraded" skip guard (which is meant for the idempotent lazy
        // upgrade path, not this button). Without it, regenerating a completed
        // meeting silently no-ops and the toast lies. (08-07-2026)
        await regen.mutateAsync({ id, force: true });
      } else {
        await reprocess.mutateAsync(id);
      }
      toast({
        variant: "success",
        title:
          variant === "regenerate" && isNotes
            ? NOTES_REGENERATE_COPY.successTitle
            : CONFIRM_COPY[variant].successTitle,
      });
      if (variant === "delete") onDeleted?.(id);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Action failed",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    } finally {
      setVariant(null);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Meeting actions"
            onClick={(e) => e.stopPropagation()}
            className="h-8 w-8 rounded-full"
          >
            <Ellipsis className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()} className="w-64">
          {onView ? (
            <>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  onView(id);
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                View details
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          ) : null}

          {/* U5 (DES 03-11-2026): two clear items, "Rename meeting" and "Reset layout". */}
          {onRename || onResetLayout ? (
            <>
              {onRename ? (
                <DropdownMenuItem onSelect={() => deferAfterDropdownMenu(onRename)}>
                  <PenLine className="mr-2 h-4 w-4" />
                  Rename meeting
                </DropdownMenuItem>
              ) : null}
              {onResetLayout ? (
                <DropdownMenuItem disabled={resetLayoutDisabled} onSelect={onResetLayout}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset layout
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator />
            </>
          ) : null}

          {withShare && !shareMenu && meetingShareRights(meeting.sharing).canSeeAccess ? (
            <MeetingShareItems
              meetingId={id}
              sharing={meeting.sharing}
              separator={!(onEditTags || tagsMenu)}
            />
          ) : null}

          {onEditTags || tagsMenu || shareMenu ? (
            <>
              {tagsMenu ?? null}
              {onEditTags && !tagsMenu ? (
                <DropdownMenuItem
                  // No preventDefault: the menu closes, then the dialog opens.
                  onSelect={() => deferAfterDropdownMenu(onEditTags)}
                >
                  <Tag className="mr-2 h-4 w-4" />
                  Tags
                </DropdownMenuItem>
              ) : null}
              {shareMenu ? (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Share2 className="mr-2 h-4 w-4" />
                    Share
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="p-1">{shareMenu}</DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : null}
              <DropdownMenuSeparator />
            </>
          ) : null}

          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Intelligence
          </DropdownMenuLabel>

          {/* A Notes meeting exports its notes (the same export route; parity
              walk, sharing v2 21-10-2026). Never for VERBLIZR staff (B2/W4). */}
          {isAdmin ? null : (
          <>
          <DropdownMenuItem
            disabled={!isReady || exportMom.isPending || !exportOk}
            onSelect={(e) => {
              e.preventDefault();
              void handleExport();
            }}
          >
            {exportMom.isPending ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ArrowDownToLine className="mr-2 h-4 w-4" />
            )}
            {isNotes ? "Export notes PDF" : "Export MoM PDF"}
          </DropdownMenuItem>

          <DropdownMenuItem
            disabled={!canExportTranscript || exportTranscript.isPending || !exportOk}
            onSelect={(e) => {
              e.preventDefault();
              deferAfterDropdownMenu(() => void handleExportTranscript());
            }}
          >
            {/* MessagesSquare — the glyph mobile's own export chooser puts on
                its Transcript row (MeetingDetailsModals). ScrollText was a
                web-only invention. MoM keeps the download arrow
                (ArrowDownToLine since DES 08-10-2026, was FileDown): mobile
                marks Export with it too.
                (02-08-2026) */}
            <MessagesSquare className="mr-2 h-4 w-4" />
            Export transcript PDF…
          </DropdownMenuItem>
          {exportOk ? null : (
            <p className="px-2 pb-1.5 pt-0.5 text-xs text-muted-foreground">{meetingExport.exportLine}</p>
          )}
          </>
          )}

          {/* Notes speaker naming (22-10-2026): owner only, while the notes
              are not being written. */}
          {onNameSpeakers && isNotes ? (
            <DropdownMenuItem
              disabled={isBusy || (meeting.status !== "ready" && !isFailed)}
              onSelect={() => deferAfterDropdownMenu(onNameSpeakers)}
            >
              <UserRoundPen className="mr-2 h-4 w-4" />
              {isBusy ? "Notes are being written…" : "Name speakers"}
            </DropdownMenuItem>
          ) : null}

          {isFailed ? (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                deferAfterDropdownMenu(() => setVariant("retry"));
              }}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry processing
            </DropdownMenuItem>
          ) : null}

          {/* Super Admin only (DES 22-10-2026): each is a paid re-run. */}
          {isAdmin ? (
            <DropdownMenuItem
              disabled={isBusy}
              onSelect={(e) => {
                e.preventDefault();
                deferAfterDropdownMenu(() => setVariant("regenerate"));
              }}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {isNotes ? "Regenerate notes" : "Regenerate intelligence"}
            </DropdownMenuItem>
          ) : null}

          {isNotes || !isAdmin ? null : (
            <DropdownMenuItem
              disabled={isBusy || !hasRecording}
              onSelect={(e) => {
                e.preventDefault();
                deferAfterDropdownMenu(() => setVariant("reprocess"));
              }}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Reprocess diarization
            </DropdownMenuItem>
          )}

          {canDelete ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  deferAfterDropdownMenu(() => setVariant("delete"));
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete meeting
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={variant !== null}
        onOpenChange={(open) => {
          if (!open) setVariant(null);
        }}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
            <AlertDialogDescription>{copy?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={activeMutation?.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={activeMutation?.isPending}
              className={cn(
                variant === "delete" &&
                  "bg-destructive text-destructive-foreground hover:bg-destructive/90",
              )}
            >
              {activeMutation?.isPending ? (
                <>
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  Working…
                </>
              ) : (
                copy?.action
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
